// @ts-check
import required from 'utils/required';
import moment from 'moment';

import {
  RECEIVE_SCHEDULE_BASE_DATA,
  REQUESTING_APPOINTMENT_SLOTS,
  RECEIVE_APPOINTMENT_SLOTS,
  NAVIGATE_DATE,
  UPDATE_VIEW,
  SELECT_EVENT,
  NEW_SLOT_SELECTION,
  HIDE_MODAL,
  CHANGE_SELECTED_PROVIDER_EMPLOYMENT,
  CHANGE_SELECTED_LOCATION,
  CREATE_SLOT_SUCCESS,
  UPDATE_SLOT_SUCCESS,
  DESTROY_SLOT_SUCCESS,
  DESTROY_SLOT_FAILURE,
} from 'agent_portal/actions/CalendarActions';

const initialState = {
  currentFetchSlotsRequestId: null,
  hasLoaded: false,
  newSlotReducerOpen: false,
  viewSlotReducerOpen: false,
  viewingSlotId: null,
  selectionStart: null,
  selectionEnd: null,
  selectedProviderEmploymentId: null,
  selectedLocationId: null,
  isFetching: false,
  date: null,
  view: 'week',
  appointmentSlots: [],
  providerEmployments: [],
  destroyFailed: null,
  destroyFailureReason: null,
  showDestroySuccess: null,
  showSlotTakenNotice: null,
  referralIdThatTookSlot: null, // relevant when a slot is taken in background and we need to show a link to a referral
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case RECEIVE_SCHEDULE_BASE_DATA:
      return handleReceiveScheduleBaseData(state, action.payload);
    case NAVIGATE_DATE:
      return handleNavigateDate(state, action.payload);
    case CHANGE_SELECTED_PROVIDER_EMPLOYMENT:
      return handleChangeSelectedProviderEmployment(state, action.payload);
    case CHANGE_SELECTED_LOCATION:
      return handleChangeSelectedLocation(state, action.payload);
    case UPDATE_VIEW:
      return handleUpdateView(state, action.payload);
    case SELECT_EVENT:
      return handleSelectEvent(state, action.payload);
    case NEW_SLOT_SELECTION:
      return handleNewSlotSelection(state, action.payload);
    case HIDE_MODAL:
      return handleHideModal(state);
    case REQUESTING_APPOINTMENT_SLOTS:
      return handleRequestingAppointmentSlots(state, action.payload);
    case RECEIVE_APPOINTMENT_SLOTS:
      return handleReceiveAppointmentSlots(state, action.payload);
    case CREATE_SLOT_SUCCESS:
      return handleCreateSlotSuccess(state, action.payload);
    case UPDATE_SLOT_SUCCESS:
      return handleUpdateSlotSuccess(state, action.payload);
    case DESTROY_SLOT_SUCCESS:
      return handleDestroySlotSuccess(state, action.payload);
    case DESTROY_SLOT_FAILURE:
      return handleDestroySlotFailure(state, action.payload);
    case 'SHOW_MODAL':
      return handleShowModal(state, action.payload);
    default:
      return state;
  }
};

const handleReceiveScheduleBaseData = (state, payload) => {
  const { providerEmployments = required('providerEmployments'), now = required('now') } = payload;
  const autoSelectedProviderEmploymentId = (providerEmployments[0] || {}).providerEmploymentId;
  const autoSelectedLocationId = providerEmployments[0]
    ? (providerEmployments[0].locations[0] || {}).id
    : null;
  return {
    ...state,
    date: moment(now),
    providerEmployments,
    selectedProviderEmploymentId: autoSelectedProviderEmploymentId,
    selectedLocationId: autoSelectedLocationId,
  };
};

const handleUpdateView = (state, payload) => {
  const { newView = required('newView') } = payload;
  return {
    ...state,
    view: newView,
  };
};

const handleSelectEvent = (state, payload) => {
  const { slotInfo = required('slotInfo') } = payload;
  return {
    ...state,
    viewSlotModalOpen: true,
    destroyFailed: null,
    destroyFailureReason: null,
    showDestroySuccess: null,
    viewingSlotId: slotInfo.id,
    showSlotTakenNotice: null,
    referralIdThatTookSlot: null,
  };
};

const handleNewSlotSelection = (state, payload) => {
  const { slotInfo = required('slotInfo') } = payload;
  return {
    ...state,
    newSlotModalOpen: true,
    slotInfo,
  };
};

const handleCreateSlotSuccess = (state, payload) => {
  const { appointmentSlot = required('appointmentSlot') } = payload;
  return {
    ...state,
    newSlotModalOpen: false,
    appointmentSlots: state.appointmentSlots.concat([appointmentSlot]),
  };
};

const handleReceiveAppointmentSlots = (state, payload) => {
  const {
    appointmentSlots = required('appointmentSlots'),
    requestId = required('requestId'),
  } = payload;
  if (state.currentFetchSlotsRequestId !== requestId) {
    return state;
  }
  return {
    ...state,
    hasLoaded: true,
    isFetching: false,
    appointmentSlots,
  };
};

const handleHideModal = state => {
  return {
    ...state,
    newSlotModalOpen: false,
    viewSlotModalOpen: false,
  };
};

const handleChangeSelectedLocation = (state, payload) => {
  const { locationId = required('locationId') } = payload;
  return {
    ...state,
    selectedLocationId: locationId,
  };
};

const handleRequestingAppointmentSlots = (state, payload) => {
  const { requestId = required('requestId') } = payload;
  return {
    ...state,
    currentFetchSlotsRequestId: requestId,
    isFetching: true,
  };
};

const handleNavigateDate = (state, payload) => {
  const { newDate = required('newDate') } = payload;
  return {
    ...state,
    date: newDate,
  };
};

const handleUpdateSlotSuccess = (state, payload) => {
  const { appointmentSlot = required('appointmentSlot') } = payload;
  return {
    ...state,
    viewSlotModalOpen: false,
    appointmentSlots: state.appointmentSlots.map(slot =>
      slot.id === appointmentSlot.id ? appointmentSlot : slot
    ),
  };
};

const handleDestroySlotSuccess = (state, payload) => {
  const { appointmentSlotId = required('appointmentSlotId') } = payload;
  const newSlots = state.appointmentSlots.filter(slot => slot.id !== appointmentSlotId);
  return {
    ...state,
    appointmentSlots: newSlots,
    showDestroySuccess: true,
  };
};

const handleDestroySlotFailure = (state, payload) => {
  const {
    reason = required('reason'),
    referralIdThatTookSlot = required('referralIdThatTookSlot'),
  } = payload;
  return {
    ...state,
    destroyFailed: true,
    destroyFailureReason: reason,
    showSlotTakenNotice: true,
    referralIdThatTookSlot,
  };
};

const handleShowModal = (state, payload) => {
  return {
    ...state,
    destroyFailed: null,
    destroyFailureReason: null,
  };
};

const handleChangeSelectedProviderEmployment = (state, payload) => {
  const { providerEmploymentId = required('providerEmploymentId') } = payload;
  return {
    ...state,
    selectedProviderEmploymentId: providerEmploymentId,
  };
};

export default reducer;
